import React, { useState } from 'react';
import { Box, Button, CircularProgress, Link, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AuthShell from '../components/auth/AuthShell';
import PasswordField from '../components/auth/PasswordField';
import { errorBoxSx, primaryButtonSx, underlineStyle } from '../components/auth/authStyles';
import { useAuth } from '../contexts/AuthContext';
import FaceRetouchingNaturalOutlinedIcon from '@mui/icons-material/FaceRetouchingNaturalOutlined';
import { isCancelled, passkeysSupported } from '../utils/passkeys';

export default function LoginPage() {
    const { t } = useTranslation();
    const { login, verifyTwoFactor, loginWithPasskey } = useAuth();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const [form, setForm] = useState({ email: '', password: '' });
    const [error, setError] = useState(params.get('expired') ? t('auth.login.sessionExpired') : null);
    const [loading, setLoading] = useState(false);
    // Second step: set once the password is accepted but 2FA is required.
    const [challenge, setChallenge] = useState(null);
    const [code, setCode] = useState('');

    const onChange = (e) => {
        setForm(f => ({ ...f, [e.target.name]: e.target.value }));
        if (error) setError(null);
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!form.email.trim() || !form.password) {
            setError(t('auth.login.missingFields'));
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await login(form.email.trim(), form.password);
            if (data.twoFactorRequired) { setChallenge(data.challenge); setLoading(false); return; }
            navigate('/dashboard', { replace: true });
        } catch (err) {
            const c = err?.response?.data?.code;
            setError(c === 'TOO_MANY_ATTEMPTS' ? t('auth.login.tooManyAttempts') : t('auth.login.invalid'));
            setLoading(false);
        }
    };

    const passkeyLogin = async () => {
        setLoading(true);
        setError(null);
        try {
            await loginWithPasskey();
            navigate('/dashboard', { replace: true });
        } catch (err) {
            if (!isCancelled(err)) {
                const c = err?.response?.data?.code;
                setError(c === 'TOO_MANY_ATTEMPTS' ? t('auth.login.tooManyAttempts') : t('auth.login.passkeyFailed'));
            }
            setLoading(false);
        }
    };

    const submitCode = async (e) => {
        e?.preventDefault();
        if (code.trim().length < 6) { setError(t('auth.twoFactor.enterCode')); return; }
        setLoading(true);
        setError(null);
        try {
            await verifyTwoFactor(challenge, code.trim());
            navigate('/dashboard', { replace: true });
        } catch (err) {
            const c = err?.response?.data?.code;
            if (c === 'CHALLENGE_INVALID') {
                setChallenge(null); setCode(''); setForm({ email: form.email, password: '' });
                setError(t('auth.twoFactor.expired'));
            } else {
                setError(c === 'TOO_MANY_ATTEMPTS' ? t('auth.login.tooManyAttempts') : t('auth.twoFactor.invalid'));
            }
            setLoading(false);
        }
    };

    if (challenge) {
        return (
            <AuthShell formMaxWidth={380}>
                <Box component="form" onSubmit={submitCode} noValidate>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', mb: 0.5 }}>{t('auth.twoFactor.title')}</Typography>
                    <Typography variant="body2" sx={{ color: '#7f8c9a', mb: 4 }}>{t('auth.twoFactor.loginSubtitle')}</Typography>
                    {error && <Box sx={errorBoxSx}><Typography variant="body2" color="error">{error}</Typography></Box>}
                    <TextField
                        fullWidth variant="standard" autoFocus label={t('auth.twoFactor.code')} value={code}
                        onChange={(e) => { setCode(e.target.value.replace(/\s/g, '')); if (error) setError(null); }}
                        inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 10 }}
                        sx={{ mb: 1, ...underlineStyle }}
                    />
                    <Typography variant="caption" sx={{ color: '#9aa7b2' }}>{t('auth.twoFactor.recoveryHint')}</Typography>
                    <Button type="submit" fullWidth variant="contained" disabled={loading} sx={{ ...primaryButtonSx, mt: 3 }}>
                        {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : t('auth.twoFactor.verify')}
                    </Button>
                    <Typography variant="body2" sx={{ textAlign: 'center', mt: 3 }}>
                        <Link component="button" type="button" onClick={() => { setChallenge(null); setCode(''); setError(null); }} sx={{ color: '#427898', fontWeight: 600 }}>
                            {t('common.back')}
                        </Link>
                    </Typography>
                </Box>
            </AuthShell>
        );
    }

    return (
        <AuthShell formMaxWidth={380}>
            <Box component="form" onSubmit={submit} noValidate>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#1a2332', mb: 0.5 }}>{t('auth.login.title')}</Typography>
                <Typography variant="body2" sx={{ color: '#7f8c9a', mb: 4 }}>{t('auth.login.subtitle')}</Typography>

                {error && <Box sx={errorBoxSx}><Typography variant="body2" color="error">{error}</Typography></Box>}

                <TextField
                    fullWidth variant="standard" name="email" type="email" autoComplete="username" autoFocus
                    label={t('auth.fields.email')} value={form.email} onChange={onChange}
                    sx={{ mb: 3, ...underlineStyle }}
                />
                <PasswordField
                    name="password" autoComplete="current-password"
                    label={t('auth.fields.password')} value={form.password} onChange={onChange}
                    sx={{ mb: 4 }}
                />
                <Button type="submit" fullWidth variant="contained" disabled={loading} sx={primaryButtonSx}>
                    {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : t('auth.login.submit')}
                </Button>
                {/* Face ID / fingerprint: no email or password; only for people who enabled it in Security. */}
                {passkeysSupported() && (
                    <Button fullWidth variant="outlined" onClick={passkeyLogin} disabled={loading} startIcon={<FaceRetouchingNaturalOutlinedIcon />}
                            sx={{ mt: 1.5, py: 1.1, borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}>
                        {t('auth.login.passkey')}
                    </Button>
                )}
                <Typography variant="body2" sx={{ textAlign: 'center', mt: 3, color: '#7f8c9a' }}>
                    {t('auth.login.noAccount')}{' '}
                    <Link component={RouterLink} to="/register" sx={{ color: '#427898', fontWeight: 600 }}>{t('auth.login.registerLink')}</Link>
                </Typography>
            </Box>
        </AuthShell>
    );
}
